import { Component } from '@theme/component';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Countdown timer.
 *
 * Supports three modes:
 * - `fixed`: counts down to the `deadline` attribute (ISO 8601 with the shop's UTC offset).
 * - `daily`: counts down to `deadline`'s time of day, rolling forward to the next occurrence.
 * - `evergreen`: counts down `duration` hours from the visitor's first view, persisted per browser.
 *
 * The rendered digits are `aria-hidden`; a static visually hidden sentence carries the meaning so
 * the surrounding `aria-live` region is never spammed.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} timer
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [expiredMessage]
 *
 * @extends {Component<Refs>}
 */
export class NcnCountdown extends Component {
  requiredRefs = ['timer', 'hours', 'minutes', 'seconds'];

  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {number} */
  #deadline = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#deadline = this.#resolveDeadline();

    if (!this.#deadline) return;

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), SECOND);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#interval);
  }

  /**
   * Resolves the timestamp the timer counts down to.
   * @returns {number} A timestamp in milliseconds, or 0 when it cannot be resolved.
   */
  #resolveDeadline() {
    const mode = this.getAttribute('mode');

    if (mode === 'evergreen') return this.#resolveEvergreenDeadline();

    const deadline = Date.parse(this.getAttribute('deadline') ?? '');

    if (Number.isNaN(deadline)) return 0;
    if (mode !== 'daily') return deadline;

    // The rendered date comes from a cacheable page, so roll forward to the next occurrence.
    const elapsed = Date.now() - deadline;

    return elapsed < 0 ? deadline : deadline + Math.ceil(elapsed / DAY) * DAY;
  }

  /**
   * Resolves (and persists) the deadline for an evergreen timer.
   * @returns {number} A timestamp in milliseconds, or 0 when it cannot be resolved.
   */
  #resolveEvergreenDeadline() {
    const duration = Number(this.getAttribute('duration')) * HOUR;
    const key = this.getAttribute('storage-key');

    if (!duration || !key) return 0;

    try {
      const stored = Number(localStorage.getItem(key));

      if (stored) return stored;

      const deadline = Date.now() + duration;
      localStorage.setItem(key, `${deadline}`);

      return deadline;
    } catch {
      // Storage is unavailable (private browsing, blocked cookies) — fall back to a fresh countdown.
      return Date.now() + duration;
    }
  }

  #tick() {
    const remaining = this.#deadline - Date.now();

    if (remaining <= 0) {
      this.#expire();
      return;
    }

    const { days } = this.refs;
    const totalHours = Math.floor(remaining / HOUR);

    this.#setValue(days, Math.floor(remaining / DAY));
    this.#setValue(this.refs.hours, days ? totalHours % 24 : totalHours);
    this.#setValue(this.refs.minutes, Math.floor(remaining / MINUTE) % 60);
    this.#setValue(this.refs.seconds, Math.floor(remaining / SECOND) % 60);
  }

  /**
   * Writes a zero-padded value, skipping identical writes to avoid needless repaints.
   * @param {HTMLElement} [element]
   * @param {number} [value]
   */
  #setValue(element, value) {
    if (!element) return;

    const text = `${value}`.padStart(2, '0');

    if (element.textContent !== text) element.textContent = text;
  }

  #expire() {
    clearInterval(this.#interval);

    // Leave expired timers on screen in the theme editor so the block stays selectable.
    if (window.Shopify?.designMode) return;

    const behavior = this.getAttribute('expired-behavior');

    if (behavior === 'zeros') {
      for (const ref of [this.refs.days, this.refs.hours, this.refs.minutes, this.refs.seconds]) {
        this.#setValue(ref, 0);
      }
      return;
    }

    if (behavior === 'message' && this.refs.expiredMessage) {
      this.refs.timer.hidden = true;
      this.refs.expiredMessage.hidden = false;
      return;
    }

    this.#removeBlock();
  }

  /**
   * Drops the block this countdown belongs to. Inside the announcement bar the remaining slides are
   * handed back the bar, since the visible one may have been the block just removed.
   */
  #removeBlock() {
    const root = this.closest('.ncn-countdown-block') ?? this;
    const isAnnouncement = root.classList.contains('announcement-bar__slide');
    const section = root.closest('.shopify-section');
    const container = root.parentElement;

    root.remove();

    if (!isAnnouncement || !container) return;

    const slides = [...container.children];
    const [first] = slides;

    // Removing the only announcement would leave an empty bar behind.
    if (!first) {
      section?.remove();
      return;
    }

    // The visible slide is picked server-side, and it may have been the one just removed.
    if (!slides.some((element) => element.getAttribute('aria-hidden') === 'false')) {
      first.setAttribute('aria-hidden', 'false');
    }
  }
}

if (!customElements.get('ncn-countdown')) {
  customElements.define('ncn-countdown', NcnCountdown);
}
